import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchChildren,
  assignChildren,
  reassignChildren,
  removeChildren,
  fetchPIAB,
  replacePIAB,
  deletePIAB,
  searchParentLines,
  type ReplacePIABRequest,
} from '@/api/piab'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { useDebounce } from './useDebounce'
import { AxiosError } from 'axios'

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.detail) {
    const detail = error.response.data.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0]?.msg ?? 'An error occurred'
    }
  }
  return 'An error occurred'
}

// ---------------------------------------------------------------------------
// Children
// ---------------------------------------------------------------------------

export function useChildren(
  planId: string | undefined,
  versionId: string | undefined,
  lineId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.children.byParent(planId!, versionId!, lineId!),
    queryFn: () => fetchChildren(planId!, versionId!, lineId!),
    enabled: !!planId && !!versionId && !!lineId,
  })
}

export function useAssignChildren(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      childLineItemIds,
      keepPiabQuantities,
    }: {
      lineId: string
      childLineItemIds: string[]
      keepPiabQuantities?: boolean
    }) =>
      assignChildren(planId, versionId, lineId, {
        child_line_item_ids: childLineItemIds,
        keep_piab_quantities: keepPiabQuantities,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.children.byParent(
          planId,
          versionId,
          variables.lineId
        ),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
      toast.success('Parent SKU assigned successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useReassignChildren(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      childLineItemIds,
      keepPiabQuantities,
    }: {
      lineId: string
      childLineItemIds: string[]
      keepPiabQuantities?: boolean
    }) =>
      reassignChildren(planId, versionId, lineId, {
        child_line_item_ids: childLineItemIds,
        keep_piab_quantities: keepPiabQuantities,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.children.byParent(
          planId,
          versionId,
          variables.lineId
        ),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
      toast.success('Parent SKU reassigned successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useRemoveChildren(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      childLineItemIds,
    }: {
      lineId: string
      childLineItemIds: string[]
    }) =>
      removeChildren(planId, versionId, lineId, {
        child_line_item_ids: childLineItemIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

// ---------------------------------------------------------------------------
// PIAB
// ---------------------------------------------------------------------------

export function usePIAB(
  planId: string | undefined,
  versionId: string | undefined,
  lineId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.piab.byParent(planId!, versionId!, lineId!),
    queryFn: () => fetchPIAB(planId!, versionId!, lineId!),
    enabled: !!planId && !!versionId && !!lineId,
  })
}

export function useReplacePIAB(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      body,
    }: {
      lineId: string
      body: ReplacePIABRequest
    }) => replacePIAB(planId, versionId, lineId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.piab.byParent(planId, versionId, variables.lineId),
      })
      // Invalidate DAG table to refresh parent FOB and all computed values
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useDeletePIAB(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (lineId: string) => deletePIAB(planId, versionId, lineId),
    onSuccess: (_data, lineId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.piab.byParent(planId, versionId, lineId),
      })
      // Invalidate DAG table to refresh parent FOB and all computed values
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

// ---------------------------------------------------------------------------
// Parent Line Search
// ---------------------------------------------------------------------------

export function useParentLineSearch(
  planId: string | undefined,
  versionId: string | undefined,
  query: string
) {
  const debouncedQuery = useDebounce(query, 300)

  return useQuery({
    queryKey: queryKeys.parentLineSearch.byQuery(
      planId!,
      versionId!,
      debouncedQuery
    ),
    queryFn: () => searchParentLines(planId!, versionId!, debouncedQuery),
    enabled: !!planId && !!versionId,
    staleTime: 0,
  })
}
